import React, { FunctionComponent } from 'react';
import { Link } from 'react-router-dom';
import { Grid, Card, Image, Icon } from 'semantic-ui-react';
import { FormattedDate, FormattedNumber } from 'react-intl';
import classnames from 'classnames';
import withStyles from 'react-jss';
import { pathOr, propOr } from 'ramda';

import { ClassProp } from 'helpers/types';
import { theme } from 'config';

import styles from './MerchantCard.styles';
import { Merchant, CardStatus } from './MerchantCard.helper';

type Props = {
  merchant: Merchant;
  classes: ClassProp<typeof styles>;
};

const renderExtraContent = (
  label: string,
  value: boolean | string | number,
  classes: ClassProp<typeof styles>,
  Component: any,
  options: Record<string, unknown>,
) => (
  <Card.Content extra>
    <Grid>
      <Grid.Row columns={2}>
        <Grid.Column className={classes.label}>{label}</Grid.Column>
        <Grid.Column className={classes.value} textAlign="right">
          <Component value={value} {...options} />
        </Grid.Column>
      </Grid.Row>
    </Grid>
  </Card.Content>
);

const renderOfferStatus = (
  classes: ClassProp<typeof styles>,
  active: boolean,
) => () => {
  const text = active ? 'Live' : 'Complete';
  return (
    <div className={classnames(classes.statusPill, { activePill: active })}>
      {text}
    </div>
  );
};

const renderCardNetworkStatus = (
  classes: ClassProp<typeof styles>,
  { logo, cardNetwork }: CardStatus,
  percentDropped: number,
  percentDroppedTrend: string,
) => (
  <Card.Content extra>
    <Grid>
      <Grid.Row columns={2}>
        <Grid.Column className={classes.label} width={10}>
          <Image className={classes.icon} src={logo} avatar rounded />
          {cardNetwork}
        </Grid.Column>
        <Grid.Column className={classes.value} width={6} textAlign="right">
          <FormattedNumber
            value={percentDropped}
            style="decimal"
            maximumFractionDigits={1}
          />
          %
          {percentDroppedTrend && percentDropped > 0 ? (
            //@ts-expect-error Icon
            <Icon name={`arrow ${percentDroppedTrend}`} />
          ) : null}
        </Grid.Column>
      </Grid.Row>
    </Grid>
  </Card.Content>
);

const MerchantCard: FunctionComponent<Props> = ({
  classes,
  merchant: {
    offerNetworkExternalVenueId,
    logo,
    merchantName,
    brandAssets,
    activeCampaign,
    firstLiveDate,
    totalTransactions,
    totalCashback,
    percentAuthsThatFailedToClear,
    percentDroppedTrend,
    cardNetworkStatus: { VISA, MASTERCARD, AMEX },
  },
}) => {
  const backgroundColor = pathOr(
    theme.violet,
    ['color', 'primaryColor', 'value'],
    brandAssets,
  );

  const merchantLogo = pathOr(logo, ['image', 'icon', 'path'], brandAssets);

  const backgroundStyle = {
    backgroundColor,
  };

  return (
    <Card
      as={Link}
      to={`merchant/${offerNetworkExternalVenueId}/overview/${merchantName}#Sales`}
      link
      className={classes.card}
      key={offerNetworkExternalVenueId}
    >
      <Card.Content
        textAlign="center"
        className={classes.cardHeader}
        style={backgroundStyle}
      >
        <Image src={merchantLogo} className={classes.logo} />
        <Card.Header>{merchantName}</Card.Header>
      </Card.Content>
      {renderExtraContent(
        'Offer Status',
        activeCampaign,
        classes,
        renderOfferStatus(classes, activeCampaign),
        {},
      )}
      {renderExtraContent(
        'Cash Back',
        totalCashback || 0,
        classes,
        FormattedNumber,
        {
          style: 'currency',
          currency: 'USD',
          currencyDisplay: 'symbol',
        },
      )}
      {renderExtraContent(
        'Transactions',
        totalTransactions || 0,
        classes,
        FormattedNumber,
        {
          style: 'decimal',
        },
      )}
      {renderCardNetworkStatus(
        classes,
        VISA,
        propOr(0, 'visa', percentAuthsThatFailedToClear),
        propOr('even', 'visa', percentDroppedTrend),
      )}
      {renderCardNetworkStatus(
        classes,
        MASTERCARD,
        propOr(0, 'mastercard', percentAuthsThatFailedToClear),
        propOr('even', 'mastercard', percentDroppedTrend),
      )}
      {renderCardNetworkStatus(
        classes,
        AMEX,
        propOr(0, 'amex', percentAuthsThatFailedToClear),
        propOr('even', 'amex', percentDroppedTrend),
      )}
      <Card.Content extra textAlign="center">
        <Grid>
          <Grid.Row>
            <Grid.Column className={classes.onboarded}>
              Onboarded On:&nbsp;
              {firstLiveDate && (
                <FormattedDate
                  value={firstLiveDate}
                  year="numeric"
                  month="short"
                  day="2-digit"
                />
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Card.Content>
    </Card>
  );
};

export default withStyles(styles)(MerchantCard);
